<template>
  <label
    class="checkbox-wrapper"
    :class="{
      '-checked': isChecked,
      '-disabled': disabled,
    }"
    :style="checkboxStyle"
  >
    <input
      v-show="!hideCheckbox"
      :id="id"
      ref="input"
      type="checkbox"
      class="checkbox-input"
      :checked="isChecked"
      :disabled="disabled"
      :required="required"
      :aria-invalid="ariaInvalid"
      @change.stop="onChange"
    />
    <span v-show="!hideCheckbox" class="checkbox-box">
      <svg
        v-if="isChecked"
        class="checkbox-icon"
        width="10"
        height="8"
        viewBox="0 0 10 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <span v-if="label" :class="labelClass">
      <span class="label-content" v-html="label"></span>
      <span v-if="required" class="required-asterisk">*</span>
    </span>
  </label>
</template>

<script>
import { colord } from "colord";

export default {
  name: "GlobalUICheckbox",
  props: {
    id: {
      type: String,
      default: "",
    },
    modelValue: {
      type: Boolean,
      default: undefined,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideCheckbox: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    ariaInvalid: {
      type: String,
      default: "false",
    },
    color: {
      type: String,
      default: "#007aff",
    },
    labelColor: {
      type: String,
      default: "#1d1d1f",
    },
    labelClass: {
      type: String,
      default: "checkbox-label",
    },
  },
  emits: ["update:modelValue", "change"],
  computed: {
    isChecked() {
      return this.modelValue !== undefined ? this.modelValue : this.checked;
    },
    checkboxStyle() {
      return {
        "--checkbox-color": this.color,
        "--checkbox-hover-color": colord(this.color).darken(0.15).toHex(),
        "--checkbox-label-color": this.labelColor,
      };
    },
  },
  methods: {
    onChange(event) {
      const isChecked = event.target.checked;
      this.$emit("update:modelValue", isChecked);
      this.$emit("change", isChecked);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/abstracts/mixins";
@import "../../Forms/styles/field-shared";

.checkbox-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.checkbox-wrapper.-disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.checkbox-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.checkbox-box {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border: 1.5px solid #949494;
  border-radius: 3px;
  background-color: #fff;
  transition: all 0.15s ease-in-out;
}

.checkbox-wrapper:hover:not(.-disabled) .checkbox-box {
  background-color: #f5f5f7;
  border-color: #9a9a9a;
}

.checkbox-wrapper.-checked .checkbox-box {
  background-color: var(--checkbox-color, #007aff);
  border-color: var(--checkbox-color, #007aff);
}

.checkbox-wrapper:hover:not(.-disabled).-checked .checkbox-box {
  background-color: var(--checkbox-hover-color, #0051d5);
  border-color: var(--checkbox-hover-color, #0051d5);
}

.checkbox-input:focus-visible + .checkbox-box {
  outline: 2px solid var(--checkbox-color, #007aff);
  outline-offset: 2px;
}

.checkbox-icon {
  color: #fff;
  width: 10px;
  height: 8px;

  path {
    stroke-dasharray: 12;
    stroke-dashoffset: 12;
    animation: draw-check 0.3s ease-out forwards;
  }
}

@keyframes draw-check {
  to {
    stroke-dashoffset: 0;
  }
}

.checkbox-label {
  @include font-family("body");

  font-size: 14px;
  line-height: 21px;

  color: var(--checkbox-label-color, #1d1d1f);
}

.checkbox-wrapper.-disabled .checkbox-label {
  color: var(--checkbox-label-color, #1d1d1f);
}
</style>
